import { isAfter, isEqual } from "date-fns";
import { isEmpty } from "ramda";
import { ReactComponent as FeaturedIcon1 } from "@/assets/images/add-ons/featured-icon-1.svg";
import { ReactComponent as FeaturedIcon2 } from "@/assets/images/add-ons/featured-icon-2.svg";
import { ReactComponent as FeaturedIcon3 } from "@/assets/images/add-ons/featured-icon-3.svg";
import { ReactComponent as FeaturedIcon4 } from "@/assets/images/add-ons/featured-icon-4.svg";
import { Typography } from "@/components/atoms";
import { useProfile } from "@/hooks";
import { useGetFeaturedAddOnsQuery } from "@/redux/apis/addOn/addOnApi";
import { useAppSelector } from "@/redux/hooks";
import { featuredAddOnsSelector } from "@/redux/slices/addOn/selectors";
import { AddOnItem } from "@/redux/slices/addOn/types";
import { clsx } from "@/utils";
import { addOnsDescription } from "./fixtures";
import { renderAddOnBadge, renderAddOnIcon, renderAddOnStatus } from "./helpers";

interface RecommendedPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  setSelectedAddOn: (id: string) => void;
}

export const RecommendedPanel = ({ setSelectedAddOn, className, ...props }: RecommendedPanelProps) => {
  const { isFetching } = useGetFeaturedAddOnsQuery();
  const featuredAddOns = useAppSelector(featuredAddOnsSelector).filter((addOn) => addOn.identifier !== "offloading");

  if (isFetching || isEmpty(featuredAddOns)) return null;

  return (
    <div className={clsx("relative mt-2 flex items-center justify-center", className)} {...props}>
      <div
        className="absolute h-full w-full rounded-lg blur-sm"
        style={{ backgroundImage: "linear-gradient(45deg, #BC82F3,#F5B9EA,#5F3B6C,purple,#AA6EEE,#9155DD,#FFBA71,#C686FF)" }}
      />
      <div className="z-10 m-1 flex h-full w-full items-center justify-center rounded-lg bg-primary-light">
        <FeaturedIcon1 className="absolute right-24 top-7 lg:right-52 lg:top-14" />
        <FeaturedIcon2 className="absolute left-16 top-10 lg:left-36 lg:top-20" />
        <FeaturedIcon3 className="absolute bottom-8 left-8 lg:bottom-16 lg:left-16" />
        <FeaturedIcon4 className="absolute bottom-3.5 right-16 lg:bottom-7 lg:right-32" />
        <div className="flex h-full w-full flex-col items-center justify-center rounded-lg p-10">
          <Typography variant="h2" className="text-center text-primary">
            Most Recommended Add Ons
          </Typography>
          <Typography variant="paragraph" className="mt-2.5 text-center">
            Enhance Your Experience with Our Top-Rated Add-Ons!
          </Typography>
          <div className="mt-[30px] flex flex-col justify-center gap-5 lg:flex-row">
            {featuredAddOns.map((addOn, i) => {
              if (i < 2) return <RecommendedAddOnItem key={addOn.identifier} addOn={addOn} setSelectedAddOn={setSelectedAddOn} />;
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const RecommendedAddOnItem = ({ addOn, setSelectedAddOn }: { addOn: AddOnItem; setSelectedAddOn: (id: string) => void }) => {
  const { dateFormat } = useProfile();
  const expiryDate = new Date(addOn.freeTrialExpiry);
  const currentDate = new Date();
  const isFreeTrialValid = Boolean(addOn.freeTrialExpiry && (isEqual(expiryDate, currentDate) || isAfter(expiryDate, currentDate)));

  return (
    <div
      className="group z-10 flex max-w-[361px] cursor-pointer flex-col rounded-lg bg-white p-5 shadow-md hover:bg-neutral-surface-gray"
      onClick={() => setSelectedAddOn(addOn.identifier)}
    >
      <div className="flex flex-row items-start">
        <div className="flex items-center justify-center rounded-lg bg-primary-dark p-3 group-hover:bg-primary">
          {renderAddOnIcon(addOn.identifier)}
        </div>
        <div className="flex flex-1 justify-end">{renderAddOnStatus(addOn.enabled, isFreeTrialValid, expiryDate, dateFormat)}</div>
      </div>
      <div className="mt-4 flex flex-row items-center gap-x-2">
        <Typography variant="action" className="group-hover:text-primary">
          {addOn.name}
        </Typography>
        {renderAddOnBadge(addOn, isFreeTrialValid)}
      </div>
      <Typography variant="paragraph" className="mt-2 text-neutral-dark-gray group-hover:text-primary">
        {addOnsDescription[addOn.identifier]}
      </Typography>
    </div>
  );
};
